import { PageContainer } from "../../components/pageContainer/PageContainer";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
  InputTextArea,
  InputDate,
  InputNote,
  InputLengthIndicator,
  InputNumber,
} from "@sellar-io/components";
import dayjs from "dayjs";
import { ReactElement, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";
import { Icon, SpinnerLegacy } from "../../components";
import { AddressFragment } from "../../components/addressFragment/AddressFragment";
import { Alert } from "../../components/alert/Alert";
import { ReactHookFormSwitch } from "../../components/form/ReactHookFormSwitch";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogCTA,
} from "../../components/modal/Dialog";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import { OrderCreateConsignmentUpdateSupplierDetailsModal } from "../../components/newOrderSummary/OrderCreateConsignment/OrderCreateConsignmentUpdateSupplierDetailsModal";
import { COLLECTIONS } from "../../constants/routes";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useCollectionCreate } from "../../hooks/api/v2/collections/useCollectionCreate";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useCollectionOrderData } from "./useCollectionOrderData";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { ExpandableSection } from "../../components/expandable/ExpandableSection";
import { getItemWeight } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";

const morningTimes = ["0900", "1000", "1100", "1200"];

const afternoonTimes = ["1300", "1400", "1500", "1600", "1700"];

export function NewCollection(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const orderId: number | undefined = location.state?.orderId;
  const { business } = useSellarCurrentBusiness();
  const [supplierUpdateActive, setSupplierUpdateActive] =
    useState<boolean>(false);

  const initialData = useCollectionOrderData(orderId);

  const [discardModalActive, setDiscardModalActive] = useState<boolean>(false);
  const form = useForm<{
    latestCollectionTime: string;
    earliestCollectionTime: string;
    collectionDate: string;
    totalWeight: number;
    numberOfPackages: number;
    specialInstructions: string;
    isOverweight: boolean;
    fulfillmentType: string;
  }>({
    shouldUseNativeValidation: true,
    defaultValues: {
      fulfillmentType: "ups",
      latestCollectionTime: undefined,
      earliestCollectionTime: undefined,
      collectionDate: dayjs(initialData.dispatchDate).format("YYYY-MM-DD"),
      totalWeight: initialData.totalWeight,
      numberOfPackages: initialData.numberOfPackages,
      specialInstructions: "",
      isOverweight: initialData.overWeight,
    },
  });

  const createCollection = useCollectionCreate();

  const collectionsV2Enabled =
    useRemoteFeatureFlags().isActive("collectionsV2");

  const collectionAddressEnabled = useRemoteFeatureFlags().isActive(
    "collectionAddressCreation",
  );

  const collectionAddress =
    collectionAddressEnabled && business?.CollectionAddress
      ? business.CollectionAddress
      : business?.DeliveryAddress;

  const specialInstructions = useWatch({
    control: form.control,
    name: "specialInstructions",
  });

  return (
    <>
      <NavigationBar
        title={"New collection"}
        leftIcon={"CLOSE"}
        onClickLeftIcon={() =>
          form.formState.isDirty
            ? setDiscardModalActive(true)
            : navigate(COLLECTIONS)
        }
      />
      <PageContainer className={"flex flex-col gap-4"}>
        {createCollection.isPending ? (
          <SpinnerLegacy />
        ) : (
          <>
            {collectionsV2Enabled && <LinkedOrder orderId={orderId} />}
            <form
              onSubmit={form.handleSubmit((data) => {
                createCollection.mutate(
                  {
                    latestCollectionTime: data.latestCollectionTime,
                    earliestCollectionTime: data.earliestCollectionTime,
                    collectionDate: dayjs(data.collectionDate).toDate(),
                    totalWeight: data.totalWeight,
                    numberOfPackages: data.numberOfPackages,
                    specialInstructions: data.specialInstructions,
                    isOverweight: data.isOverweight,
                    status: "active",
                    orderId,
                  },
                  {
                    onSuccess: (res) => navigate(`${COLLECTIONS}/${res.id}`),
                  },
                );
              })}
              className={"flex flex-col"}
            >
              {createCollection.isError && (
                <Alert
                  type={"error"}
                  message={
                    apiV2AssertError(createCollection.error)?.response?.data
                      .message ?? "An unknown error occurred"
                  }
                />
              )}
              <div className="flex flex-col gap-2">
                <Card className="bg-white">
                  <CardHeader className="flex flex-col bg-white p-4">
                    <H5>Collection info</H5>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2 p-4">
                    <Alert
                      className={"w-full"}
                      message={
                        "UPS can collect multiple shipments on a given day. Ensure you indicate the combined weight and number of packages."
                      }
                      title={
                        "Have you already scheduled a collection for UPS for this day?"
                      }
                      type={"information"}
                    />
                    <InputLabel>Service provider</InputLabel>
                    <InputContainer>
                      <InputSelect
                        {...form.register("fulfillmentType", {
                          required: true,
                          disabled: true,
                        })}
                      >
                        <InputOption value={"ups"}>UPS</InputOption>
                      </InputSelect>
                    </InputContainer>
                    <InputLabel className={"mt-2"}>
                      Total weight of your collection (kg)
                    </InputLabel>
                    <InputContainer>
                      <InputNumber
                        {...form.register("totalWeight", {
                          valueAsNumber: true,
                          required: true,
                          min: 0,
                        })}
                        placeholder={"0"}
                      />
                    </InputContainer>
                    <InputNote>
                      The combined weigh of each of your shipments for this
                      collection
                    </InputNote>
                    <InputLabel className={"mt-2"}>
                      Number of packages
                    </InputLabel>
                    <InputContainer>
                      <InputNumber
                        {...form.register("numberOfPackages", {
                          valueAsNumber: true,
                          required: true,
                          min: 0,
                          max: 30,
                        })}
                        placeholder={"0"}
                      />
                    </InputContainer>
                    <InputNote>Maximum: 30</InputNote>
                    <H5>
                      Does this collection contain individual items over 32kg?
                      (Such as steel kegs and casks)
                    </H5>
                    <ReactHookFormSwitch
                      control={form.control}
                      name={`isOverweight`}
                    />
                    <InputLabel className={"mt-2"}>Collection Date</InputLabel>
                    <InputContainer>
                      <InputDate
                        className={"w-full"}
                        {...form.register("collectionDate", {
                          required: true,
                          valueAsDate: true,
                        })}
                        min={
                          dayjs().isBefore(dayjs().hour(12))
                            ? dayjs().format("YYYY-MM-DD")
                            : dayjs().add(1, "day").format("YYYY-MM-DD")
                        }
                      />
                    </InputContainer>
                    <InputNote>
                      Same day collection is only available if you are booking
                      your collection before midday. Weekend collections
                      currently unavailable.
                    </InputNote>
                    <div className="mt-2 grid md:grid-cols-2 md:gap-6">
                      <div className="col-span-1">
                        <InputLabel>Earliest collection time</InputLabel>
                        <InputContainer>
                          <InputSelect
                            {...form.register("earliestCollectionTime", {
                              required: true,
                            })}
                          >
                            {morningTimes.map((time, index) => (
                              <InputOption key={index} value={time}>
                                {time}
                              </InputOption>
                            ))}
                          </InputSelect>
                        </InputContainer>
                        <InputNote>Must be before midday</InputNote>
                      </div>
                      <div className="col-span-1">
                        <InputLabel>Latest collection time</InputLabel>
                        <InputContainer>
                          <InputSelect
                            {...form.register("latestCollectionTime", {
                              required: true,
                            })}
                          >
                            {afternoonTimes.map((time, index) => (
                              <InputOption key={index} value={time}>
                                {time}
                              </InputOption>
                            ))}
                          </InputSelect>
                        </InputContainer>
                        <InputNote>Must be after midday</InputNote>
                      </div>
                    </div>
                    <InputLabel className={"mt-2"}>
                      Special instructions
                    </InputLabel>
                    <InputContainer>
                      <InputTextArea
                        variant="legacyFixed"
                        {...form.register("specialInstructions", {
                          required: false,
                        })}
                        maxLength={57}
                        placeholder={
                          "Provide any instructions to help the driver with this collection."
                        }
                      />
                    </InputContainer>
                    <InputLengthIndicator
                      currentLength={specialInstructions.length}
                      maxLength={57}
                    />
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardHeader
                    className={twMerge(
                      "bg-white",
                      "flex items-center justify-between p-4",
                    )}
                  >
                    <div className="flex flex-col">
                      <H5>Your Contact Info</H5>
                      <H6>
                        The email address & phone number you provide will
                        receive notifications about your collection.
                      </H6>
                    </div>
                    <Button
                      color={"secondary"}
                      onClick={() => setSupplierUpdateActive(true)}
                    >
                      <Icon icon={"PENCIL"} />
                      Edit
                    </Button>
                  </CardHeader>
                  <CardContent className={"p-4"}>
                    <div className="grid md:grid-cols-2">
                      <div className="col-span-1 flex flex-col">
                        <div className="flex gap-2">
                          <H5>Collection address</H5>
                        </div>
                        {collectionAddress && (
                          <AddressFragment address={collectionAddress} />
                        )}
                      </div>
                      <div className="col-span-1 flex flex-col gap-2">
                        <div className="">
                          <H5>Email Address</H5>
                          <H6>{business?.email}</H6>
                        </div>
                        <div className="">
                          <H5>Phone number</H5>
                          <H6>{business?.phoneNumber}</H6>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <div className="flex justify-end">
                  <Button type={"submit"}>Book collection</Button>
                </div>
              </div>
            </form>
          </>
        )}
      </PageContainer>
      <Dialog
        active={discardModalActive}
        onDismiss={() => setDiscardModalActive(false)}
      >
        <DialogNavigation
          title={"Discard collection?"}
          description={"Are you sure you wish to discard this collection?"}
        />
        <DialogCTA>
          <Button onClick={() => navigate(COLLECTIONS)} color={"red"}>
            Discard
          </Button>
        </DialogCTA>
      </Dialog>
      {business?.Supplier && (
        <OrderCreateConsignmentUpdateSupplierDetailsModal
          onDismiss={() => setSupplierUpdateActive(false)}
          active={supplierUpdateActive}
          email={business.email}
          phoneNumber={business.phoneNumber}
          businessId={business.id}
        />
      )}
    </>
  );
}

const LinkedOrder = ({ orderId }: { orderId?: number }) => {
  const [expanded, setExpanded] = useState(false);

  const orderQuery = useOrder(orderId ?? 0);
  const { data, isLoading } = orderQuery;

  if (!orderId) {
    return (
      <Alert
        type="warning"
        title="No linked order"
        message="You are creating a collection without a linked order. If you want to link an order go to your sales and create a collection via the prompt there."
      />
    );
  }

  if (isLoading) return <></>;

  return (
    <ExpandableSection
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}
      title={`${data?.Retailer.Business.name} - #${orderId} - Order details`}
      className="cursor-pointer rounded-lg bg-white p-4"
    >
      <div className="rounded-lg border border-zinc-300">
        <div className="grid grid-cols-3 rounded-t-lg border-b border-zinc-300 bg-zinc-100 p-1 font-semibold">
          <span>Item</span>
          <span>Quantity</span>
          <span>Estimated weight</span>
        </div>
        {data?.OrderItems.map((item, idx) => (
          <div
            key={idx}
            className={twMerge(
              "grid grid-cols-3 p-1",
              idx !== data.OrderItems.length - 1 && "border-b border-zinc-300",
            )}
          >
            <span>{item.Product.name}</span>
            <span>{item.quantity}</span>
            <span>{getItemWeight(item.Product) * item.quantity}kg</span>
          </div>
        ))}
      </div>
    </ExpandableSection>
  );
};
